import {
  Text,
  Box,
  Flex,
  Spinner,
  Link,
  useBreakpoint,
  Grid,
} from "@chakra-ui/react";
import useResizeObserver from "@react-hook/resize-observer";
import {
  CSSProperties,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { isMobile, isTablet } from "react-device-detect";
import { CUSTOM_CARD_TYPES } from "../../../components/cards/constants";
import { ContentCard } from "../../../components/cards/ContentCard";
import { HeroCardContainer } from "../../../components/cards/HeroCardContainer";
import { ButtonCarousel } from "../../../components/carousel/ButtonCarousel";
import { SectionTitle } from "../../../components/text/SectionTitle";
import {
  Article,
  Artist,
  Bundle,
  Comic,
  ContentSectionCustomCard,
  Release,
  Section,
  GCChannel,
  NavigationSource,
} from "../../../types";
import {
  useIsMediumBreakpoint,
  useIsSmallBreakpoint,
} from "../../../utils/useBreakpoints";
import { ComicReleaseCalendarContainer } from "../../../components/cards/ComicReleaseCalendarContainer";
import { ArrowTraditionalIcon } from "../../../components/icons/ArrowTraditionalIcon";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router";
import { useCardsSizing } from "../../reader/hooks/hookstate/useCalendarSizing";
import { FeatureCardContainer } from "../../../components/cards/FeatureCardContainer";
import { StreamLinksContainer } from "../../../components/cards/StreamLinkContainer";
import { WindowVirtualGrid } from "../../../components/grid/WindowVirtualizer";
import { useAccountData } from "../../../hooks/useAccountData";
import { createStableAnalyticsTrigger } from "../../../analytics/stableAnalyticsTrigger";
import {
  createVisibleSectionIndexSet,
  getLastVisibleSectionIndex,
  updateVisibleSectionIndexSet,
} from "../../../analytics/recsys/visibleSections";
import {
  measureItemHorizontal,
  measureItemVertical,
} from "./sectionMeasurements";
import {
  trackRecsysItemLastViewForSection,
  trackRecsysItemListViewForSection,
  trackRecsysSectionLastViewFromVisibleIndex,
  trackRecsysSectionListViewFromVisibleIndex,
  trackRecsysSectionViewAllClick,
} from "../../../analytics/recsys/tracking";

const CUSTOM_CARD_TYPE_SET = new Set<string>(Object.values(CUSTOM_CARD_TYPES));

interface ContentCardGridProps {
  data: (Section | undefined)[];
  isLoading: boolean;
  isFetchingNextPage?: boolean;
  hasNextPage: boolean;
  onFetchNextPage?: () => void;
  onScroll?: (scrollableRef: HTMLDivElement) => void;
  navbarCollapsed?: boolean;
  positionOffset?: CSSProperties["height"];
  header?: React.ReactNode;
  activeChannel?: GCChannel;
  currentChannelSlug?: string;
  currentSlug?: string;
  activeStreamId?: string;
  /** Callback for scroll restoration - reports first visible index */
  onVisibleIndexChange?: (index: number) => void;
  /** Index to restore scroll to on back navigation */
  restoreToIndex?: number;
}

export function ContentCardGrid({
  data,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  onFetchNextPage,
  onScroll,
  positionOffset = 0,
  header,
  activeChannel,
  currentChannelSlug,
  currentSlug,
  activeStreamId,
  onVisibleIndexChange,
  restoreToIndex,
}: ContentCardGridProps) {
  const isSmallScreen = useIsSmallBreakpoint();
  const isMediumScreen = useIsMediumBreakpoint();
  const location = useLocation();

  const firstSectionIsHero =
    data[0]?.meta.layout.style.context === CUSTOM_CARD_TYPES.hero;

  // Memoize navigationSource to prevent re-creating on every render
  const navigationSource = useMemo<NavigationSource>(
    () => ({
      sourceChannelSlug: currentChannelSlug,
      sourceSlug: currentSlug,
      sourceChannelName:
        activeChannel?.display_name === "GlobalComix"
          ? "Home"
          : activeChannel?.display_name ||
            (currentChannelSlug === "originals"
              ? "Originals"
              : currentChannelSlug
                ? currentChannelSlug.charAt(0).toUpperCase() +
                  currentChannelSlug.slice(1)
                : "Home"),
      sourcePath: location.pathname,
    }),
    [
      currentChannelSlug,
      currentSlug,
      activeChannel?.display_name,
      location.pathname,
    ],
  );

  const [lastVisibleSectionIndex, setLastVisibleSectionIndex] = useState(0);
  const lastVisibleSectionId = data[lastVisibleSectionIndex]?.id;
  const lastVisibleSectionIndexRef = useRef(lastVisibleSectionIndex);
  const lastVisibleSectionIdRef = useRef(lastVisibleSectionId);
  const visibleSectionIndicesRef = useRef(createVisibleSectionIndexSet());
  const stableSectionListViewTriggerRef = useRef(
    createStableAnalyticsTrigger({
      delayMs: 1500,
      onStable: () => {
        trackRecsysSectionListViewFromVisibleIndex(
          activeStreamIdRef.current,
          lastVisibleSectionIndexRef.current,
          lastVisibleSectionIdRef.current,
        );
      },
    }),
  );
  const activeStreamIdRef = useRef(activeStreamId);
  const hasSectionsRef = useRef(data.length > 0);

  const handleSectionVisibilityChange = useCallback(
    (sectionIndex: number, isVisible: boolean) => {
      visibleSectionIndicesRef.current = updateVisibleSectionIndexSet(
        visibleSectionIndicesRef.current,
        sectionIndex,
        isVisible,
      );

      const nextLastVisibleSectionIndex = getLastVisibleSectionIndex(
        visibleSectionIndicesRef.current,
      );

      if (nextLastVisibleSectionIndex === undefined) {
        return;
      }

      lastVisibleSectionIndexRef.current = nextLastVisibleSectionIndex;
      lastVisibleSectionIdRef.current = data[nextLastVisibleSectionIndex]?.id;
      setLastVisibleSectionIndex((currentLastVisibleSectionIndex) =>
        currentLastVisibleSectionIndex === nextLastVisibleSectionIndex
          ? currentLastVisibleSectionIndex
          : nextLastVisibleSectionIndex,
      );
    },
    [data],
  );

  useEffect(() => {
    lastVisibleSectionIndexRef.current = lastVisibleSectionIndex;
    lastVisibleSectionIdRef.current = lastVisibleSectionId;
  }, [lastVisibleSectionId, lastVisibleSectionIndex]);

  useEffect(() => {
    activeStreamIdRef.current = activeStreamId;
  }, [activeStreamId]);

  useEffect(() => {
    hasSectionsRef.current = data.length > 0;
  }, [data.length]);

  useEffect(() => {
    stableSectionListViewTriggerRef.current.update(
      lastVisibleSectionId !== undefined,
      `${activeStreamId ?? "unknown"}:${String(lastVisibleSectionId)}:${lastVisibleSectionIndex}`,
    );
  }, [activeStreamId, lastVisibleSectionId, lastVisibleSectionIndex]);

  useEffect(() => {
    const stableSectionListViewTrigger =
      stableSectionListViewTriggerRef.current;
    return () => {
      stableSectionListViewTrigger.dispose();
    };
  }, []);

  useEffect(() => {
    const emitSectionLastView = () => {
      if (!hasSectionsRef.current) {
        return;
      }

      trackRecsysSectionLastViewFromVisibleIndex(
        activeStreamIdRef.current,
        lastVisibleSectionIndexRef.current,
        lastVisibleSectionIdRef.current,
      );
    };

    const supportsNativeScrollEnd =
      "onscrollend" in (window as unknown as Record<string, unknown>);
    if (supportsNativeScrollEnd) {
      window.addEventListener("scrollend", emitSectionLastView, {
        passive: true,
      });
      return () => {
        window.removeEventListener("scrollend", emitSectionLastView);
      };
    }

    let scrollStopTimer: number | undefined;

    const onWindowScroll = () => {
      if (scrollStopTimer) {
        window.clearTimeout(scrollStopTimer);
      }

      scrollStopTimer = window.setTimeout(() => {
        emitSectionLastView();
      }, 150);
    };

    window.addEventListener("scroll", onWindowScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onWindowScroll);
      if (scrollStopTimer) {
        window.clearTimeout(scrollStopTimer);
      }
    };
  }, []);

  return (
    <WindowVirtualGrid
      header={
        <>
          {header}
          {!header && !firstSectionIsHero && !isLoading && (
            <Box pt={{ base: 4, lg: 8 }} />
          )}
          {isLoading && (
            <Flex
              direction="row"
              alignItems="center"
              justifyContent="center"
              flexGrow={1}
              minHeight="200px"
            >
              <Spinner
                color="blaze.blaze"
                size="xl"
                thickness="4px"
                speed="0.65s"
              />
            </Flex>
          )}
        </>
      }
      onScroll={onScroll}
      overscan={3}
      pageBuffer={3}
      direction="vertical"
      scrollContainerHeight={`calc(100vh - ${positionOffset})`}
      scrollContainerWidth="100%"
      measureElement={(el) => {
        const index = el.getAttribute("data-index") ?? -1;
        const item = data[index as number];

        const { mode, context, size } = item?.meta.layout.style ?? {};
        const isCustomCard = CUSTOM_CARD_TYPE_SET.has(context as string);
        const isHeroCard = context === CUSTOM_CARD_TYPES.hero;

        const isStreamLinkSection = context === CUSTOM_CARD_TYPES.streamLinks;

        if (isStreamLinkSection && size === "s" && !isSmallScreen) {
          // hide old stream link section on desktop
          return 0;
        }

        const showComicReleaseCalendarVertical =
          mode === "list" && size === "r_list" && isMediumScreen;

        if (showComicReleaseCalendarVertical) {
          return (
            el.children[0].getBoundingClientRect().height +
            (isSmallScreen ? 48 : 64)
          );
        }
        const bottomPadding = isSmallScreen ? 48 : 64;

        if (mode === "grid" || mode === "horizontal") {
          // Hero card currently has horizontal mode set so it meets the condition
          if (isHeroCard) return el.children[0].getBoundingClientRect().height;

          if (el.children[0].children[1]) {
            return (
              el.children[0].getBoundingClientRect().height + bottomPadding
            );
          }
        }

        if (isCustomCard || mode === "calendar") {
          return el.children[0].getBoundingClientRect().height + bottomPadding;
        }

        return measureItemVertical(item);
      }}
      estimateSize={(index) => {
        const item = data[index];

        const { mode, context } = item?.meta.layout.style ?? {};
        const isCustomCard = CUSTOM_CARD_TYPE_SET.has(context as string);
        const isHeroCard = context === CUSTOM_CARD_TYPES.hero;

        if (isCustomCard || mode === "calendar") {
          if (isHeroCard) {
            return 100;
          }
          return -1;
        }

        return measureItemVertical(item);
      }}
      totalCount={data.length}
      data={data}
      isFetchingNextPage={isFetchingNextPage}
      onFetchNextPage={onFetchNextPage}
      hasNextPage={hasNextPage}
      renderItem={(item, index) => {
        if (item)
          return (
            <ContentRow
              item={item}
              sectionIndex={index}
              navigationSource={navigationSource}
              onSectionVisibilityChange={handleSectionVisibilityChange}
            />
          );
      }}
      onVisibleIndexChange={(index) => {
        onVisibleIndexChange?.(index);
      }}
      restoreToIndex={restoreToIndex}
    />
  );
}

function getSubItems(
  section: Section,
  hiddenReadingHistoryComics: number[] = [],
): (Comic | Artist | Article | Release | Bundle | ContentSectionCustomCard)[] {
  let items: (
    | Comic
    | Artist
    | Article
    | Release
    | Bundle
    | ContentSectionCustomCard
  )[] = [];

  switch (section.meta.layout.style.context) {
    case "comics":
      items = section.comics;
      if (section.source_type === "continue-reading") {
        items = (items as Comic[]).filter(
          (comic) => !hiddenReadingHistoryComics.includes(comic.id),
        );
      }
      return items;
    case "creators":
      return section.artists;
    case "news":
      return section.articles;
    case "releases":
      return section.releases;
    case "bundle":
      return section.bundles;
    case CUSTOM_CARD_TYPES.hero:
    case CUSTOM_CARD_TYPES.featuredCarousel:
    case CUSTOM_CARD_TYPES.featuredSeriesRoadblock:
    case CUSTOM_CARD_TYPES.streamLinks:
      return section.cards;
    default:
      return [];
  }
}

const TrackableContentCard = memo(
  ({
    content,
    index,
    layout,
    mode,
    section,
    sectionIndex,
    size,
    context,
  }: {
    content:
      | Comic
      | Artist
      | Article
      | Release
      | Bundle
      | ContentSectionCustomCard;
    index: number;
    layout: Section["meta"]["layout"];
    mode: string;
    section: Section;
    sectionIndex?: number;
    size: string;
    context: string;
  }) => {
    return (
      <Box data-context={context} data-contentsize={size} data-mode={mode}>
        <ContentCard
          content={content}
          layout={layout}
          section={section}
          index={index}
          sectionIndex={sectionIndex}
        />
      </Box>
    );
  },
);

TrackableContentCard.displayName = "TrackableContentCard";

const ContentRow = memo(
  ({
    item,
    sectionIndex,
    navigationSource,
    onSectionVisibilityChange,
  }: {
    item: Section;
    sectionIndex?: number;
    navigationSource: NavigationSource;
    onSectionVisibilityChange?: (
      sectionIndex: number,
      isVisible: boolean,
    ) => void;
  }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const stableItemRef = useRef(item);
    const stableSectionIndexRef = useRef(sectionIndex);
    const lastVisibleItemIndexRef = useRef(0);
    const stableItemListViewTriggerRef = useRef(
      createStableAnalyticsTrigger({
        delayMs: 1500,
        onStable: () => {
          const trackedSectionIndex = stableSectionIndexRef.current;
          if (trackedSectionIndex === undefined) {
            return;
          }

          trackRecsysItemListViewForSection(
            stableItemRef.current,
            trackedSectionIndex,
            lastVisibleItemIndexRef.current,
          );
        },
      }),
    );
    const [scrollByCount, setScrollByCount] = useState(1);
    const [isSectionVisible, setIsSectionVisible] = useState(false);
    const [isCarouselInteracting, setIsCarouselInteracting] = useState(false);
    const [selectedCarouselIndex, setSelectedCarouselIndex] = useState(0);
    const [itemListViewResetKey, setItemListViewResetKey] = useState(0);
    const isSmallScreen = useIsSmallBreakpoint();
    const navigate = useNavigate();
    const breakpoint = useBreakpoint({ ssr: false });
    const isMediumScreen = useIsMediumBreakpoint();
    const accountData = useAccountData();

    let columnCount = 3;
    if (["xs", "sm"].includes(breakpoint)) columnCount = 1;
    else if (["md", "lg"].includes(breakpoint)) columnCount = 2;
    else if (["xl", "2xl"].includes(breakpoint)) columnCount = 3;

    const { t } = useTranslation();

    const parentItem = item;
    const itemWidth = measureItemHorizontal(parentItem);

    const setCalendarScale = useCardsSizing();

    const renderSmallScreenRow = isMobile || isTablet || isSmallScreen;

    const subGridData = useMemo(
      () => getSubItems(item, accountData?.hidden_reading_history_comics ?? []),
      [item, accountData?.hidden_reading_history_comics],
    );

    const { context, mode, size } = parentItem.meta.layout.style;
    const { display } = parentItem.meta.layout;
    const isHeroCard = context === CUSTOM_CARD_TYPES.hero;
    const isFeatureCard =
      context === CUSTOM_CARD_TYPES.featuredCarousel ||
      context === CUSTOM_CARD_TYPES.featuredSeriesRoadblock;

    const isRoadblock = context === CUSTOM_CARD_TYPES.featuredSeriesRoadblock;

    const isStreamLinkSection = context === CUSTOM_CARD_TYPES.streamLinks;

    const isContentCalendarSection = mode === "calendar";

    const isComicReleaseListSection = mode === "list" && size === "r_list";

    useResizeObserver(containerRef, (entry) => {
      const numItemsVisible = entry.contentRect.width / (itemWidth + 20);
      setScrollByCount(Math.floor(numItemsVisible) - 1);

      if (isContentCalendarSection || isComicReleaseListSection) {
        setCalendarScale(entry.contentRect.width, itemWidth);
      }
    });

    const isContinueReading = item.source_type === "continue-reading";
    const isListEmptyAfterFiltering = subGridData.length === 0;
    const hadComicsInOriginalList = item.comics && item.comics.length > 0;

    useEffect(() => {
      stableItemRef.current = item;
      stableSectionIndexRef.current = sectionIndex;
    }, [item, sectionIndex]);

    useEffect(() => {
      const node = containerRef.current;
      if (!node) {
        return;
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          const isVisible = Boolean(entry?.isIntersecting);
          setIsSectionVisible(isVisible);

          const trackedSectionIndex = stableSectionIndexRef.current;
          if (trackedSectionIndex !== undefined) {
            onSectionVisibilityChange?.(trackedSectionIndex, isVisible);
          }
        },
        { threshold: 0.1 },
      );

      observer.observe(node);
      return () => {
        const trackedSectionIndex = stableSectionIndexRef.current;
        if (trackedSectionIndex !== undefined) {
          onSectionVisibilityChange?.(trackedSectionIndex, false);
        }

        observer.disconnect();
      };
    }, [onSectionVisibilityChange]);
    const subGridItems = useMemo(
      () =>
        subGridData.map((item, index) => (
          <TrackableContentCard
            key={item.id}
            content={item}
            context={context}
            index={index}
            layout={parentItem.meta.layout}
            mode={mode}
            section={parentItem}
            sectionIndex={sectionIndex}
            size={size}
          />
        )),
      [subGridData, context, size, mode, parentItem, sectionIndex],
    );

    useEffect(() => {
      lastVisibleItemIndexRef.current = Math.min(
        subGridData.length - 1,
        selectedCarouselIndex + Math.max(scrollByCount, 1) - 1,
      );
    }, [scrollByCount, selectedCarouselIndex, subGridData.length]);

    useEffect(() => {
      const stableKey = `${itemListViewResetKey}:${selectedCarouselIndex}:${scrollByCount}:${subGridData.length}`;
      stableItemListViewTriggerRef.current.update(
        sectionIndex !== undefined &&
          subGridData.length > 0 &&
          isSectionVisible &&
          !isCarouselInteracting,
        stableKey,
      );
    }, [
      isCarouselInteracting,
      isSectionVisible,
      itemListViewResetKey,
      scrollByCount,
      sectionIndex,
      selectedCarouselIndex,
      subGridData.length,
    ]);

    useEffect(() => {
      const stableItemListViewTrigger = stableItemListViewTriggerRef.current;
      return () => {
        stableItemListViewTrigger.dispose();
      };
    }, []);

    if (isContinueReading && isListEmptyAfterFiltering) {
      if (hadComicsInOriginalList) {
        // If the list had comics but is now empty, it means the user hid them all.
        // Show the empty state message.
        return (
          <Box
            ref={containerRef}
            pl={renderSmallScreenRow ? 4 : undefined}
            userSelect="none"
            mb={{ base: 12, lg: 16 }}
          >
            <SectionTitle
              mb={renderSmallScreenRow ? 6 : 8}
              subtitle={item.description}
            >
              {item.display_name}
            </SectionTitle>
            <Flex
              justify="center"
              align="center"
              bg="transparent.white.10"
              w="full"
              h="100px"
              borderRadius="10px"
              alignItems="center"
              justifyContent="center"
              mr="16px"
            >
              <Text
                textAlign="center"
                fontSize="14px"
                fontWeight="500"
                fontStyle="normal"
                color="neutral.300"
              >
                {t("screens.root.allComicsHiddenFromReadingHistory")}
              </Text>
            </Flex>
          </Box>
        );
      } else {
        // If the list was empty from the beginning, render nothing.
        return null;
      }
    }

    if (isContentCalendarSection) {
      return (
        <Box
          ref={containerRef}
          pl={renderSmallScreenRow ? 4 : undefined}
          mb={{ base: 12, lg: 16 }}
          userSelect="none"
        >
          <ComicReleaseCalendarContainer
            data={subGridData as Comic[]}
            layout={parentItem.meta.layout}
            section={item}
            sectionIndex={sectionIndex}
          />
        </Box>
      );
    }

    if (isStreamLinkSection) {
      const size = parentItem.meta.layout.style.size;

      return (
        <Box ref={containerRef} pl={renderSmallScreenRow ? 4 : undefined}>
          {size === "s" && (
            <SectionTitle
              mb={renderSmallScreenRow ? 6 : 8}
              subtitle={item.description}
            >
              {item.display_name}
            </SectionTitle>
          )}

          <StreamLinksContainer
            data={subGridData as ContentSectionCustomCard[]}
            layout={parentItem.meta.layout.style.mode}
            size={size === "s" || size === "m" ? size : "m"}
          />
        </Box>
      );
    }

    if (isHeroCard) {
      return (
        <Box ref={containerRef}>
          <HeroCardContainer
            data={subGridData as ContentSectionCustomCard[]}
            section={item}
            sectionIndex={sectionIndex}
          />
        </Box>
      );
    } else if (isFeatureCard) {
      return (
        <Box
          ref={containerRef}
          backgroundColor={isSmallScreen ? "charcoal.900" : "transparent"}
          height="fit-content"
          userSelect="none"
        >
          {isSmallScreen ? (
            <Box
              borderBottomRightRadius="30px"
              height="30px"
              mb={{ base: 12, lg: 16 }}
              backgroundColor="charcoal.charcoal"
            />
          ) : null}
          <Box
            pl={renderSmallScreenRow ? 4 : undefined}
            py={isRoadblock && !isSmallScreen ? "64px" : undefined}
          >
            {(!isRoadblock || isSmallScreen) && (
              <SectionTitle
                mb={renderSmallScreenRow ? 6 : 8}
                subtitle={item.description}
              >
                {item.display_name}
              </SectionTitle>
            )}
            <FeatureCardContainer
              data={subGridData as ContentSectionCustomCard[]}
              scrollByCount={scrollByCount}
              isRoadblock={isRoadblock}
              section={item}
              sectionIndex={sectionIndex}
            />
          </Box>
          <Box
            borderTopRightRadius="30px"
            height={{ base: "30px", lg: "0px" }}
            mt={{ base: 12, lg: 0 }}
            boxShadow={
              isSmallScreen
                ? "1px -1px 1px 0px var(--chakra-colors-transparent-white-20)"
                : "none"
            }
            backgroundColor={
              isSmallScreen ? "charcoal.charcoal" : "transparent"
            }
          />
        </Box>
      );
    }

    const showComicReleaseListSectionAsGrid =
      isComicReleaseListSection && isMediumScreen;

    /// Vertical cards have a hover animation that adds a margin on top, so section title margin needs to be adjusted
    const isVerticalGridCard =
      ((mode === "grid" || mode === "horizontal") && size === "m") ||
      size === "l";

    const sectionTitleMargin =
      (renderSmallScreenRow ? 6 : 8) - (isVerticalGridCard ? 2 : 0);

    const linkUrl = new URL(parentItem.results_url);
    return (
      <Box ref={containerRef} userSelect="none">
        <Box pl={renderSmallScreenRow ? 4 : undefined}>
          <SectionTitle
            mb={sectionTitleMargin}
            subtitle={item.description}
            action={
              display?.include_view_all ? (
                <Link
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();

                    if (sectionIndex !== undefined) {
                      trackRecsysSectionViewAllClick({
                        section: item,
                        sectionIndex,
                      });
                    }

                    navigate(linkUrl.pathname + linkUrl.search, {
                      state: navigationSource,
                    });
                  }}
                  variant="brand"
                  mr={isSmallScreen ? "16px" : "38px"}
                  mt="2px"
                  color="dune.700"
                  fontSize={renderSmallScreenRow ? "12px" : "14px"}
                  fontFamily="Roboto"
                  fontWeight="500"
                  letterSpacing="0.5"
                  lineHeight={renderSmallScreenRow ? "14.06px" : "16.41px"}
                >
                  <Flex direction="row" alignItems="center">
                    <Text whiteSpace="nowrap">{t("screens.root.viewAll")}</Text>
                    <ArrowTraditionalIcon
                      boxSize={renderSmallScreenRow ? "16px" : "24px"}
                      ml="4px"
                    />
                  </Flex>
                </Link>
              ) : null
            }
          >
            {item.display_name}
          </SectionTitle>
        </Box>
        <Box position="relative" role="group">
          {showComicReleaseListSectionAsGrid ? (
            <Grid
              templateColumns={`repeat(${columnCount}, 1fr)`}
              gap={4}
              autoRows="1fr"
              justifyContent="center"
            >
              {subGridItems}
            </Grid>
          ) : (
            <Box
              onPointerDown={() => setIsCarouselInteracting(true)}
              onPointerUp={() => {
                setIsCarouselInteracting(false);
                setItemListViewResetKey((current) => current + 1);
              }}
              onPointerCancel={() => {
                setIsCarouselInteracting(false);
                setItemListViewResetKey((current) => current + 1);
              }}
              onWheel={() => {
                setItemListViewResetKey((current) => current + 1);
              }}
            >
              <ButtonCarousel
                width="100%"
                slidesToScroll={scrollByCount}
                containerProps={{
                  pl: renderSmallScreenRow ? 4 : undefined,
                  gap: renderSmallScreenRow ? 1.5 : 6,
                }}
                includeSpacer={renderSmallScreenRow}
                showScrollbar={renderSmallScreenRow}
                align="center"
                big
                onEmblaSelect={(selectedIndex) => {
                  if (sectionIndex === undefined) {
                    return;
                  }

                  setSelectedCarouselIndex(selectedIndex);
                  setIsCarouselInteracting(false);
                  setItemListViewResetKey((current) => current + 1);

                  const lastVisibleItemIndex = Math.min(
                    subGridData.length - 1,
                    selectedIndex + Math.max(scrollByCount, 1) - 1,
                  );

                  trackRecsysItemLastViewForSection(
                    item,
                    sectionIndex,
                    lastVisibleItemIndex,
                  );
                }}
              >
                {subGridItems}
              </ButtonCarousel>
            </Box>
          )}
        </Box>
      </Box>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if item, sectionIndex, or navigationSource change
    return (
      prevProps.item === nextProps.item &&
      prevProps.sectionIndex === nextProps.sectionIndex &&
      prevProps.onSectionVisibilityChange ===
        nextProps.onSectionVisibilityChange &&
      prevProps.navigationSource.sourceChannelSlug ===
        nextProps.navigationSource.sourceChannelSlug &&
      prevProps.navigationSource.sourceSlug ===
        nextProps.navigationSource.sourceSlug &&
      prevProps.navigationSource.sourceChannelName ===
        nextProps.navigationSource.sourceChannelName &&
      prevProps.navigationSource.sourcePath ===
        nextProps.navigationSource.sourcePath
    );
  },
);
